import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup, FormControl, Validators} from '@angular/forms';
import {OptionService} from "../option/option.service";
import {TranslateService} from "@ngx-translate/core";
import {Donation} from "../donation/donation";

/**
 * Component for showing additional fields that are set via a configuration.
 */
@Component({
  selector: 'app-additionalfields',
  templateUrl: './additionalfields.component.html'
})
export class AdditionalfieldsComponent implements OnInit, OnDestroy {
  /**
   * Formgroup for validating
   */
  @Input() form: FormGroup;

  /**
   * model for saving the data to
   */
  @Input() model: any;

  /**
   * The current donation
   */
  @Input() donation: Donation;

  /**
   * List of additional fields that are visible in the contact form.
   */
  @Input() addtionalFieldsConfiguration: any = [];

  /**
   * The constructor
   * @param optionService
   * @param translate
   */
  constructor(protected optionService: OptionService, private translate: TranslateService) {
  }

  /**
   * Initialize the additional fields, so they can be used in the template.
   */
  initAdditionalFields(): void {
    if (!this.addtionalFieldsConfiguration) {
      // no config available
      return;
    }

    const currentLanguage = this.translate.currentLang;
    const fallbackLang = "en";
    for (const field of this.addtionalFieldsConfiguration) {
      // check if there is a translated label for the current language. If not try fallbacks.
      if (field.label.hasOwnProperty(currentLanguage)) {
        field.labeltext = field.label[currentLanguage];
      } else if (field.label.hasOwnProperty(fallbackLang)) {
        field.labeltext = field.label[fallbackLang];
      } else {
        field.labeltext = Object.keys(field.label).length > 0 ? field.label[Object.keys(field.label)[0]] : "";
      }

      switch (field.type) {
        case 'radio':
          for (const option of field.options) {
            // check if there is a translated label for the current language. If not try fallbacks.
            if (option.label.hasOwnProperty(currentLanguage)) {
              option.labeltext = option.label[currentLanguage];
            } else if (option.label.hasOwnProperty(fallbackLang)) {
              option.labeltext = option.label[fallbackLang];
            } else {
              option.labeltext = Object.keys(option.label).length > 0 ? option.label[Object.keys(option.label)[0]] : "";
            }
          }
          break;
        case 'select':
          field.selectOptions = [];
          for (const option of field.options) {
            let labeltext = option.value;
            // check if there is a translated label for the current language. If not try fallbacks.
            if (option.label.hasOwnProperty(currentLanguage)) {
              labeltext = option.label[currentLanguage];
            } else if (option.label.hasOwnProperty(fallbackLang)) {
              labeltext = option.label[fallbackLang];
            } else {
              labeltext = Object.keys(option.label).length > 0 ? option.label[Object.keys(option.label)[0]] : "";
            }
            field.selectOptions.push([option.value, labeltext]);
          }
          break;
      }
    }
  }

  /**
   * is called when initialising the component
   * @constructor
   */
  ngOnInit(): void {
    this.initAdditionalFields();

    // add additional fields to the form
    for (const field of this.addtionalFieldsConfiguration) {
      if (this.model.hasOwnProperty(field.name) === false) {
        this.model[field.name] = null;
      }
      this.form.addControl(field.name, new FormControl(this.model[field.name], field.required ? [Validators.required] : null));
    }

    // update field conditions initially and on changes on the form
    this.updateConditions();
    this.form.valueChanges.subscribe(() => setTimeout(() => this.updateConditions(), 0));
  }

  /**
   * when the component is destroyed.
   */
  ngOnDestroy() {
    // disable all additional fields.
    for (const field of this.addtionalFieldsConfiguration) {
      this.form.removeControl(field.name);
    }
  }

  /**
   * Updates the fields based on the conditions that are mybe defined on some other fields.
   * i.e.: bield A is only visible if field B has a certain value.
   */
  updateConditions(): void {
    for (const field of this.addtionalFieldsConfiguration) {
      if (field.hasOwnProperty("condition")) {

        let show = true;
        Object.keys(field.condition).forEach(key => {
          // get the value for the condition
          let value = this.form.get(key)?.value;

          if (key.indexOf('donation.additional') === 0) {
            // case 1: get the value from the donation
            value = this.donation.additional[key.replace('donation.additional.', '')] ?? null;
          } else if (key.indexOf('donation.') === 0) {
            // case 1: get the value from the donation
            value = this.donation[key.replace('donation.', '')] ?? null;
          } else if (key === 'gift.isGiftDonation') {
            // case 2: condition is for de/activated gift donation
            value = this.donation.giftData.isGiftDonation;
          }

          let conditionValue = field.condition[key];
          let not = false;
          if (typeof conditionValue === "string" && conditionValue.startsWith('!')) {
            conditionValue = conditionValue.substring(1);
            not = true;
          }

          // check if the current value fulfills the comndition
          if ((not === false && value !== conditionValue) || (not === true && value === conditionValue)) {
            show = false;
          }
        });
        setTimeout(() => {
          if (show === true) {
            this.form.get(field.name)?.enable({onlySelf: false, emitEvent: false});
          } else {
            // reset data and disable field, only if the field is part of the form. Otherwise, the data is deleted with onDestroy.
            if (this.form.get(field.name)) {
              delete this.model[field.name];
              this.form.get(field.name).disable({onlySelf: false, emitEvent: false});
            }
          }
        }, 0);
      }
      setTimeout(() => {
        // preset first value
        if (field.type === 'select' && !this.model[field.name]) {
          this.model[field.name] = field.options[0].value;
        }
      }, 0);
    }
  }
}
